import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  Avatar,
  Divider,
  Rating,
  Skeleton,
} from '@mui/material';
import { getBarbers } from '../../services/api';
import type { Barber } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface BarberSelectionProps {
  onSelect: (barberId: number) => void;
}

const BarberSelection: React.FC<BarberSelectionProps> = ({ onSelect }) => {
  const { t } = useLanguage();
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBarbers = async () => {
      try {
        const data = await getBarbers();
        setBarbers(data);
        
        // If there's only one barber, automatically select it
        if (data.length === 1) {
          onSelect(data[0].id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching barbers:', error);
        setLoading(false);
      }
    };
    fetchBarbers();
  }, [onSelect]);

  // If there's only one barber, don't render the selection UI
  if (barbers.length === 1 && !loading) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          color: 'text.primary',
          fontWeight: 500,
          mb: 3,
          textAlign: 'center'
        }}
      >
        {t('booking.chooseBarber')}
      </Typography>
      
      {loading ? (
        <Box>
          {[1, 2, 3].map((item) => (
            <Paper 
              key={item}
              sx={{ 
                p: 3, 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Skeleton variant="circular" width={80} height={80} sx={{ mr: 3 }} />
              <Box sx={{ width: '100%' }}>
                <Skeleton variant="text" width="40%" height={30} />
                <Skeleton variant="text" width="60%" />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Skeleton variant="rectangular" width={120} height={36} />
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {barbers.map((barber) => (
            <Grid item xs={12} sm={6} md={4} key={barber.id}>
              <Paper 
                sx={{ 
                  p: 3, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6,
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Avatar 
                    src={barber.avatarUrl} 
                    alt={barber.name}
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      border: '3px solid',
                      borderColor: 'primary.main',
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ color: 'text.primary', mb: 0.5 }}
                    >
                      {barber.name}
                    </Typography>
                    
                    {barber.rating !== undefined && (
                      <Rating 
                        value={barber.rating} 
                        readOnly 
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'center',
                      mb: 2,
                      flex: 1
                    }}
                  >
                    {barber.bio || `${barber.name} is a professional barber ready to provide you with excellent service.`}
                  </Typography>
                  
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => onSelect(barber.id)}
                    fullWidth
                  >
                    {t('button.select')}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default BarberSelection; 